import React, { useState, useEffect } from "react";
import { Edit, Plus, Trash2, FileText, X, Download } from "lucide-react";
import api, { BASE } from "../api";
import { K } from "../theme";
import { Bg, Bt, Sl, Inp } from "./index";
import { confirmDialog } from "../notify";
import { VendorModal } from "./VendorModal";
import { fmtDMY, fmtDDMMYY } from "../dateFormat";

// Vendor master list + CRUD. Shared by Settings → Vendors (Admin) and
// Expenses → Vendors (Admin + Accounts) so both surfaces stay identical.
//
// Pass `companies` from the parent so the Vendor form's "Default Company"
// picker has values; both Settings and Expenses already load companies.
export const VendorsManager = ({ companies = [] }) => {
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [edit, setEdit] = useState(null); // null | {} (new) | vendor (edit)
  const [stmt, setStmt] = useState(null); // open Statement modal for a vendor
  const [f16, setF16] = useState(null); // open Form 16A modal for a vendor
  const [msg, setMsg] = useState("");
  const [fName, setFName] = useState("");
  const [fLocation, setFLocation] = useState("");
  const [fCompany, setFCompany] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = () =>
    api.get("/api/vendors")
      .then(d => setRows(d || []))
      .catch(e => setMsg("Error: " + (e.message || "load failed")));

  useEffect(() => {
    load();
    api.get("/api/vendors/categories").then(d => setCats(d || [])).catch(() => {});
  }, []);

  const flash = (m, e = false) => { setMsg((e ? "Error: " : "") + m); setTimeout(() => setMsg(""), 3000); };

  const del = async (v) => {
    const linked = v.billCount || 0;
    const message = linked > 0
      ? `"${v.name}" has ${linked} linked bill(s) and can't be deleted. Deactivate so new bills can't use it?`
      : `Delete vendor "${v.name}"?`;
    if (!(await confirmDialog(message))) return;
    try {
      const r = await api.del("/api/vendors/" + v.id);
      flash(r.deleted ? "Vendor deleted" : `Deactivated (${r.linked} bill${r.linked === 1 ? "" : "s"})`);
      load();
    } catch (e) { flash(e.message || "Failed", true); }
  };

  const toggleActive = async (v) => {
    try { await api.put("/api/vendors/" + v.id, { is_active: !v.active }); load(); }
    catch (e) { flash(e.message || "Failed", true); }
  };

  return (
    <div>
      {/* Title and blurb in one column, the action beside them — so the
          paragraph wraps against the button instead of running under it. */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="min-w-0">
          <div className="text-[15px] font-bold text-t0">Vendors</div>
          <p className="text-[14px] text-t2 leading-[1.55] mt-[4px] mb-0">
            Master list of third-party vendors paid by Accounts. Picking a vendor on a bill auto-fills bank
            details, GSTIN and the default TDS section, so recurring bills (rent, electricity, internet) are
            one-click.
          </p>
          <p className="text-[13px] text-t3 leading-[1.55] mt-[6px] mb-0">
            A vendor can only be deleted when no bills are linked to it; otherwise it's deactivated so it
            can't be picked for new bills.
          </p>
        </div>
        <Bt s="sm" icon={Plus} onClick={() => setEdit({})}>Add Vendor</Bt>
      </div>
      <div className="flex flex-wrap gap-2 mb-3 items-end">
        <div><div className="text-[12px] text-t3 mb-[2px]">Name</div><Inp value={fName} onChange={setFName} placeholder="Search by name..." style={{ width: 180 }} /></div>
        <div><div className="text-[12px] text-t3 mb-[2px]">Location</div><Inp value={fLocation} onChange={setFLocation} placeholder="Search by state/location..." style={{ width: 180 }} /></div>
        <div><div className="text-[12px] text-t3 mb-[2px]">Company</div><Sl w={160} options={[{ v: "all", l: "All Companies" }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} value={fCompany} onChange={setFCompany} /></div>
        <div><div className="text-[12px] text-t3 mb-[2px]">Added from</div><Inp type="date" value={fFrom} onChange={setFFrom} style={{ width: 140 }} /></div>
        <div><div className="text-[12px] text-t3 mb-[2px]">Added to</div><Inp type="date" value={fTo} onChange={setFTo} style={{ width: 140 }} /></div>
        {(fName || fLocation || fCompany !== "all" || fFrom || fTo) &&
          <Bt v="ghost" s="sm" onClick={() => { setFName(""); setFLocation(""); setFCompany("all"); setFFrom(""); setFTo(""); }}>Reset</Bt>}
      </div>
      {!rows ? <div className="text-t3 text-[14px]">Loading…</div> : (() => {
        const filtered = rows.filter(v => {
          if (fName && !v.name?.toLowerCase().includes(fName.toLowerCase())) return false;
          if (fLocation && !v.state?.toLowerCase().includes(fLocation.toLowerCase())) return false;
          if (fCompany !== "all" && String(v.companyId) !== fCompany) return false;
          if (fFrom && (!v.createdAt || v.createdAt.slice(0, 10) < fFrom)) return false;
          if (fTo && (!v.createdAt || v.createdAt.slice(0, 10) > fTo)) return false;
          return true;
        });
        return (
        <div className="rounded-xl border border-b0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <thead><tr className="bg-bg2">
                <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Name</th>
                <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">GSTIN / PAN</th>
                <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Bank</th>
                <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">TDS</th>
                <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Company</th>
                <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Bills</th>
                <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Status</th>
                <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase"></th>
              </tr></thead>
              <tbody>
                {filtered.map(v => (
                  <tr key={v.id} className="border-b border-b0 hover:bg-sh">
                    <td className="px-2 py-[6px]"><div className="text-[14px] font-bold text-t0">{v.name}</div>{v.state && <div className="text-[12px] text-t3">{v.state}</div>}</td>
                    <td className="px-2 py-[6px] text-[12px] font-mono text-t2">{v.gstin || "—"}{v.pan && <div className="text-t3">{v.pan}</div>}</td>
                    <td className="px-2 py-[6px] text-[12px] text-t1">{v.bankName || "—"}{v.account && <div className="text-t3">{v.account}</div>}</td>
                    <td className="px-2 py-[6px] text-[12px] text-t1">{v.defaultTdsSection ? `${v.defaultTdsSection} · ${v.defaultTdsRate}%` : "—"}</td>
                    <td className="px-2 py-[6px] text-[14px] text-t1">{v.company || "—"}</td>
                    <td className="px-2 py-[6px] text-center text-[14px] text-t0 font-semibold">{v.billCount || 0}</td>
                    <td className="px-2 py-[6px] text-center">{v.active ? <Bg c={K.g} sm dot>Active</Bg> : <Bg c={K.t3} sm>Inactive</Bg>}</td>
                    <td className="px-2 py-[6px] text-right whitespace-nowrap">
                      <Bt v="ghost" s="xs" icon={FileText} onClick={() => setStmt(v)}>Statement</Bt>
                      <Bt v="ghost" s="xs" icon={Download} onClick={() => setF16(v)}>Form 16A</Bt>
                      <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEdit(v)} />
                      <Bt v="ghost" s="xs" onClick={() => toggleActive(v)}>{v.active ? "Deactivate" : "Activate"}</Bt>
                      <Bt v="ghost" s="xs" icon={Trash2} onClick={() => del(v)} />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="text-center p-[30px] text-t3 text-[14px]">{rows.length === 0 ? <>No vendors yet — click <b className="text-t0">Add Vendor</b>.</> : "No vendors match these filters."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ); })()}
      {msg && <div className={`mt-3 text-[14px] font-semibold ${msg.startsWith("Error") ? "text-r" : "text-g"}`}>{msg}</div>}
      {edit !== null && <VendorModal cur={edit.id ? edit : null} vendorCats={cats} companies={companies} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {stmt && <VendorStatementModal vendor={stmt} onClose={() => setStmt(null)} />}
      {f16 && <Form16AModal vendor={f16} companies={companies} onClose={() => setF16(null)} />}
    </div>
  );
};

// ── Form 16A modal — pick FY + quarter + company, download PDF cert ──
const Form16AModal = ({ vendor, companies, onClose }) => {
  const now = new Date();
  const fyStart = (now.getMonth() + 1) >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  const [fy, setFy] = useState(`${fyStart}-${String(fyStart + 1).slice(2)}`);
  const [quarter, setQuarter] = useState('Q1');
  const [cid, setCid] = useState(vendor.companyId ? String(vendor.companyId) : '');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const run = async () => {
    if (!/^\d{4}-\d{2}$/.test(fy)) return setErr('FY must be like 2025-26');
    setBusy(true); setErr('');
    const qs = new URLSearchParams({ fy, quarter });
    if (cid) qs.set('company', cid);
    try {
      const r = await fetch(`${BASE}/api/vendors/${vendor.id}/form-16a?${qs.toString()}`, { headers: { Authorization: 'Bearer ' + api.getToken() } });
      if (!r.ok) {
        let msg = 'Download failed';
        try { const j = await r.json(); if (j.error) msg = j.error; } catch (e) { /* noop */ }
        throw new Error(msg);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `form-16A-${vendor.name.replace(/\s+/g, '_')}-${fy}-${quarter}.pdf`; a.click(); URL.revokeObjectURL(url);
      onClose();
    } catch (e) { setErr(e.message || 'Download failed'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[628px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex justify-between mb-2"><span className="text-base font-extrabold text-t0">Form 16A — TDS Certificate</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
        <div className="text-[14px] text-t2 mb-4">For <b className="text-t0">{vendor.name}</b>{vendor.pan ? ` · PAN ${vendor.pan}` : ''}</div>
        <div className="grid grid-cols-2 gap-[10px] mb-3">
          <div><div className="text-[14px] text-t2 mb-[3px]">FY</div><Inp value={fy} onChange={setFy} placeholder="2025-26" /></div>
          <div><div className="text-[14px] text-t2 mb-[3px]">Quarter</div><Sl options={[{ v: 'Q1', l: 'Q1 (Apr–Jun)' }, { v: 'Q2', l: 'Q2 (Jul–Sep)' }, { v: 'Q3', l: 'Q3 (Oct–Dec)' }, { v: 'Q4', l: 'Q4 (Jan–Mar)' }]} value={quarter} onChange={setQuarter} /></div>
        </div>
        <div className="mb-3"><div className="text-[14px] text-t2 mb-[3px]">Company (deductor)</div><Sl options={[{ v: '', l: 'All companies for this vendor' }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} value={cid} onChange={setCid} /></div>
        <div className="text-[14px] text-t2 bg-bg2 border border-b0 rounded-lg px-3 py-2 mb-3">PDF certificate listing every TDS deduction on this vendor's bills in the quarter — share with the vendor for their records. Informational format, not the TRACES one.</div>
        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-[6px]"><Bt full icon={Download} onClick={run} disabled={busy}>{busy ? 'Generating…' : 'Download PDF'}</Bt><Bt v="secondary" full onClick={onClose}>Cancel</Bt></div>
      </div>
    </div>
  );
};

// ── Vendor Statement modal — full account history with running totals ──
const VendorStatementModal = ({ vendor, onClose }) => {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ from: "", to: "" });
  const fmt = (v) => '₹' + (v || 0).toLocaleString('en-IN');

  const load = (filters = f) => {
    const qs = new URLSearchParams();
    if (filters.from) qs.set('from', filters.from);
    if (filters.to) qs.set('to', filters.to);
    api.get('/api/vendors/' + vendor.id + '/statement' + (qs.toString() ? '?' + qs.toString() : ''))
      .then(d => setData(d))
      .catch(() => setData({ error: 'Failed to load statement' }));
  };
  useEffect(() => { load({ from: "", to: "" }); }, []); // eslint-disable-line

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[210] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1384px] max-h-[92vh] overflow-auto" onMouseDown={e => e.stopPropagation()}>
        <div className="p-4 border-b border-b0 flex items-start justify-between gap-2">
          <div>
            <div className="text-[17px] font-extrabold text-t0">{vendor.name}</div>
            <div className="text-[14px] text-t3 mt-[2px]">
              {vendor.gstin ? `GSTIN ${vendor.gstin}` : 'GSTIN —'}
              {vendor.state ? ` · ${vendor.state}` : ''}
              {vendor.bankName ? ` · ${vendor.bankName} ${vendor.account || ''}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="p-4">
          <div className="flex items-end gap-2 mb-3 flex-wrap">
            <div><div className="text-[12px] text-t3 mb-[2px]">From</div><Inp type="date" value={f.from} onChange={v => setF(p => ({ ...p, from: v }))} style={{ width: 140 }} /></div>
            <div><div className="text-[12px] text-t3 mb-[2px]">To</div><Inp type="date" value={f.to} onChange={v => setF(p => ({ ...p, to: v }))} style={{ width: 140 }} /></div>
            <Bt s="sm" onClick={() => load(f)}>Apply</Bt>
            <Bt v="ghost" s="sm" onClick={() => { const r = { from: "", to: "" }; setF(r); load(r); }}>Reset</Bt>
          </div>

          {!data ? <div className="text-t3 text-[14px]">Loading…</div> : data.error ? <div className="text-r text-[14px]">{data.error}</div> : <>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="bg-bg2 border border-b0 rounded-lg p-2"><div className="text-[12px] text-t3 uppercase tracking-wide">Total Billed</div><div className="text-[15px] font-extrabold text-t0">{fmt(data.totals.billed)}</div></div>
              <div className="bg-bg2 border border-b0 rounded-lg p-2"><div className="text-[12px] text-t3 uppercase tracking-wide">Paid</div><div className="text-[15px] font-extrabold text-g">{fmt(data.totals.paid)}</div></div>
              <div className="bg-bg2 border border-b0 rounded-lg p-2"><div className="text-[12px] text-t3 uppercase tracking-wide">Outstanding</div><div className="text-[15px] font-extrabold text-a">{fmt(data.totals.outstanding)}</div></div>
            </div>

            {data.rows.length === 0 ? <div className="text-center p-[30px] text-t3 text-[14px]">No bills for this vendor in the selected range.</div> :
              <div className="rounded-xl border border-b0 overflow-hidden"><div className="overflow-x-auto">
                <table className="w-full border-collapse text-[14px]">
                  <thead><tr className="bg-bg2">
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Bill Date</th>
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Bill No</th>
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Period</th>
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Category</th>
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Company</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">Total</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">TDS</th>
                    <th className="px-2 py-[6px] text-right text-t3 text-[12px] uppercase">Net Payable</th>
                    <th className="px-2 py-[6px] text-center text-t3 text-[12px] uppercase">Status</th>
                    <th className="px-2 py-[6px] text-left text-t3 text-[12px] uppercase">Payment</th>
                  </tr></thead>
                  <tbody>
                    {data.rows.map(r => <tr key={r.id} className="border-b border-b0 hover:bg-sh">
                      <td className="px-2 py-[6px] text-[14px] text-t1">{fmtDDMMYY(r.billDate) || '—'}</td>
                      <td className="px-2 py-[6px] text-[12px] font-mono text-t2">{r.billNo || '—'}</td>
                      <td className="px-2 py-[6px] text-[14px] text-t1">{r.period || '—'}</td>
                      <td className="px-2 py-[6px] text-[14px] text-t1">{r.category || '—'}</td>
                      <td className="px-2 py-[6px] text-[14px] text-t1">{r.company || '—'}</td>
                      <td className="px-2 py-[6px] text-right text-[14px] text-t0">{fmt(r.total)}</td>
                      <td className="px-2 py-[6px] text-right text-[14px] text-r">{r.tds > 0 ? '−' + fmt(r.tds) : '—'}</td>
                      <td className="px-2 py-[6px] text-right text-[14px] font-bold text-t0">{fmt(r.netPayable)}</td>
                      <td className="px-2 py-[6px] text-center"><Bg c={r.status === 'Paid' ? K.g : r.status === 'Recorded' ? K.a : K.t3} sm dot>{r.status}</Bg></td>
                      <td className="px-2 py-[6px] text-[12px] text-t2">{r.payRef || (r.paidAt ? fmtDMY(r.paidAt) : '—')}</td>
                    </tr>)}
                  </tbody>
                </table>
              </div></div>}
          </>}
        </div>
      </div>
    </div>
  );
};
